import { z } from 'zod';

import { CLINIC_USER_ROLE_ADMIN, CLINIC_USER_ROLE_OPTIONS } from '@/constants/clinicUser';

// Email: prefix (a-z, 0-9, _ . - between alphanumeric) @ domain (alphanumeric, . -) . TLD (2+ chars). Max 100. Trim.
const EMAIL_MAX_LENGTH = 100;
const EMAIL_FORMAT_REGEX =
  /^[a-zA-Z0-9]+([._-][a-zA-Z0-9]+)*@[a-zA-Z0-9]+([.-][a-zA-Z0-9]+)*\.[a-zA-Z]{2,}$/;

const clinicUserEmailSchema = z
  .string()
  .optional()
  .default('')
  .transform(s => (s ?? '').trim())
  .refine(s => s.length <= EMAIL_MAX_LENGTH, 'メールアドレスは100文字以下にしてください。')
  .refine(
    s => s === '' || EMAIL_FORMAT_REGEX.test(s),
    'メールアドレスを正しいメールアドレスにしてください。'
  );

// 権限: must be one of the values the dropdown offers. Checked with `.some()` rather than
// `value === 0 || value === 1` on purpose: TS 5.5+ infers a type predicate from that shape,
// zod then narrows the schema output to `0 | 1`, and the plain `number` the API returns for
// this field no longer fits the form type. A z.union would fit but reports zod's default
// English invalid_union message instead of the Japanese one.
const clinicUserRoleSchema = z
  .number({ required_error: '権限は必須です。', invalid_type_error: '権限は必須です。' })
  .refine(
    value => CLINIC_USER_ROLE_OPTIONS.some(option => option.value === value),
    '権限は正しい値を選択してください。'
  );

/**
 * メールアドレス is required for 管理者権限 and optional for 一般権限, so it cannot live in
 * `clinicUserEmailSchema` — a field-level rule cannot see `role`. It runs as a whole-object
 * check instead, and is shared so create and edit cannot drift apart.
 *
 * The address becomes the Cognito username the SSO link is keyed on, which is why the backend
 * makes exactly this field required for exactly this role
 * (ClinicUserRoleValidation::clinicUserEmailRules). The message is copied from there verbatim:
 * the operator must not read one wording when the form catches it and another when the server
 * does.
 */
const CLINIC_ADMIN_EMAIL_REQUIRED_MESSAGE = '管理者権限のユーザーにはメールアドレスが必須です。';

const requireEmailForClinicAdmin = (
  value: { role: number; email: string },
  ctx: z.RefinementCtx
): void => {
  if (value.role === CLINIC_USER_ROLE_ADMIN && value.email === '') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['email'],
      message: CLINIC_ADMIN_EMAIL_REQUIRED_MESSAGE,
    });
  }
};

// Schema for creating a new clinic user (password required)
export const clinicUserFormSchema = z
  .object({
    clinic_id: z
      .number({ required_error: '治療院IDは必須です。' })
      .int()
      .min(1, '治療院IDは必須です。'),
    role: clinicUserRoleSchema,
    login_id: z.string().min(1, 'ユーザーIDは必須です。').max(100, 'ユーザーIDは100文字以下にしてください。'),
    password: z.string().min(1, 'パスワードは必須です。').max(200, 'パスワードは200文字以下にしてください。'),
    name: z.string().min(1, '名前は必須です。').max(100, '名前は100文字以下にしてください。'),
    email: clinicUserEmailSchema,
    available_from: z
      .union([z.string(), z.null(), z.undefined()])
      .refine(val => val != null && String(val).trim() !== '', '有効期限FROMは必須です。'),
    available_to: z.string().nullable().optional(),
  })
  .superRefine(requireEmailForClinicAdmin);

export type ClinicUserFormData = z.infer<typeof clinicUserFormSchema>;

// Schema for editing a clinic user (password required)
export const clinicUserEditFormSchema = z.object({
  clinic_id: z
    .number({ required_error: '治療院IDは必須です。' })
    .int()
    .min(1, '治療院IDは必須です。'),
  role: clinicUserRoleSchema,
  login_id: z.string().min(1, 'ユーザーIDは必須です。').max(100, 'ユーザーIDは100文字以下にしてください。'),
  password: z.string().min(1, 'パスワードは必須です。').max(200, 'パスワードは200文字以下にしてください。'),
  name: z.string().min(1, '名前は必須です。').max(100, '名前は100文字以下にしてください。'),
  email: clinicUserEmailSchema,
  available_from: z
    .union([z.string(), z.null(), z.undefined()])
    .refine(val => val != null && String(val).trim() !== '', '有効期限FROMは必須です。'),
  available_to: z.string().nullable().optional(),
}).superRefine(requireEmailForClinicAdmin);

export type ClinicUserEditFormData = z.infer<typeof clinicUserEditFormSchema>;
